// LinkTapsPieChart.js - Pie Chart Component for Link Taps Breakdown
import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { InfoIcon } from 'icons/materialIcons';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

// Register Chart.js components for pie chart
ChartJS.register(
    ArcElement,
    ChartTooltip,
    Legend
);

const LinkTapsPieChart = ({ data = [], totalTaps: propTotalTaps }) => {
    // Color palette for different link types
    const colors = [
        'rgba(80, 189, 249, 0.8)',   // Blue
        'rgba(140, 241, 217, 0.8)',  // Green
        'rgba(238, 165, 245, 0.8)',  // Purple
        'rgba(255, 206, 84, 0.8)',   // Yellow
        'rgba(255, 99, 132, 0.8)',   // Red
        'rgba(54, 162, 235, 0.8)',   // Light Blue
        'rgba(153, 102, 255, 0.8)',  // Violet
        'rgba(255, 159, 64, 0.8)',   // Orange
    ];

    const borderColors = [
        'rgba(80, 189, 249, 1)',
        'rgba(140, 241, 217, 1)',
        'rgba(238, 165, 245, 1)',
        'rgba(255, 206, 84, 1)',
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(153, 102, 255, 1)',
        'rgba(255, 159, 64, 1)',
    ];

    // Process data for pie chart
    const processedData = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return {
                labels: ['No Data'],
                datasets: [{
                    data: [1],
                    backgroundColor: ['rgba(200, 200, 200, 0.8)'],
                    borderColor: ['rgba(200, 200, 200, 1)'],
                    borderWidth: 2
                }]
            };
        }

        // If data is already aggregated (from linkTaps in backend response)
        let processedItems;
        if (data[0] && (data[0].count !== undefined || data[0].linkType)) {
            processedItems = data.map(item => ({
                type: item.linkType || item.type || 'Unknown',
                name: item.linkName || item.name || item.linkType || 'Unknown Link',
                count: item.count || 1
            }));
        } else {
            // If data is raw tap events, group by link type
            const groupedByType = data.reduce((acc, link) => {
                const type = link.linkType || link.type || 'Unknown';

                if (!acc[type]) {
                    acc[type] = {
                        type: type,
                        count: 0,
                        name: link.linkName || link.linkTitle || type
                    };
                }

                acc[type].count += 1;
                return acc;
            }, {});

            processedItems = Object.values(groupedByType);
        }

        // Sort by count (descending) and limit to top 8 items
        processedItems = processedItems
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);

        const labels = processedItems.map(item => item.name);
        const counts = processedItems.map(item => item.count);
        const backgroundColors = processedItems.map((_, index) => colors[index % colors.length]);
        const borderColorsArray = processedItems.map((_, index) => borderColors[index % borderColors.length]);

        return {
            labels,
            datasets: [{
                data: counts,
                backgroundColor: backgroundColors,
                borderColor: borderColorsArray,
                borderWidth: 2
            }]
        };
    }, [data]);

    // Chart options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    font: {
                        size: 12,
                        family: 'Inter, sans-serif'
                    },
                    color: '#333333',
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#ffffff',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        const total = context.dataset.data.reduce((sum, value) => sum + value, 0);
                        const percentage = ((context.parsed / total) * 100).toFixed(1);
                        return `${context.label}: ${context.parsed} taps (${percentage}%)`;
                    }
                }
            }
        },
        elements: {
            arc: {
                borderWidth: 2
            }
        }
    };    // Calculate total taps for display - use prop if available, otherwise calculate from data
    const totalTaps = useMemo(() => {
        // First priority: use the prop value if provided
        if (propTotalTaps !== undefined && propTotalTaps !== null) {
            return propTotalTaps;
        }

        if (!data || !Array.isArray(data) || data.length === 0) {
            return 0;
        }

        // If data is already aggregated (from linkTaps in backend response), sum the counts
        if (data[0] && (data[0].count !== undefined || data[0].linkType)) {
            return data.reduce((sum, item) => sum + (item.count || 1), 0);
        }

        // If data is raw tap events, count all events
        return data.length;
    }, [data, propTotalTaps]);

    return (
        <Box
            sx={{
                backgroundColor: '#ffffff',
                borderRadius: '18px',
                padding: '24px',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '24px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#333333' }}>
                        Link Taps Distribution
                    </Typography>
                    <Tooltip title="Breakdown of link taps by link type">
                        <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                    </Tooltip>
                </Box>
                <Typography sx={{ fontSize: '14px', color: '#828282' }}>
                    Total: {totalTaps} taps
                </Typography>
            </Box>

            {/* Chart Container */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px'
                }}
            >
                {totalTaps > 0 ? (
                    <Box sx={{ width: '100%', height: '300px', position: 'relative' }}>
                        <Pie data={processedData} options={options} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '16px'
                        }}
                    >
                        <Typography sx={{ fontSize: '16px', color: '#828282' }}>
                            No link taps data available
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#999999' }}>
                            Link tap data will appear here when available
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default LinkTapsPieChart;
